import { type Theme, alpha, type Components } from '@mui/material/styles';
import { svgIconClasses } from '@mui/material/SvgIcon';
import { dividerClasses } from '@mui/material/Divider';
import { listItemIconClasses } from '@mui/material/ListItemIcon';
import { menuItemClasses } from '@mui/material/MenuItem';
import { tabClasses } from '@mui/material/Tab';

import { IconChevronDown } from '@/components/icon/tabler/IconChevronDown';
import { OutlineStepIcon } from '@/components/icon/controlIcons';
import { IconChevronLeft } from '@/components/icon/tabler/IconChevronLeft';
import { IconChevronRight } from '@/components/icon/tabler/IconChevronRight';
import { IconChevronsLeft } from '@/components/icon/tabler/IconChevronsLeft';
import { IconChevronsRight } from '@/components/icon/tabler/IconChevronsRight';
import { IconDots } from '@/components/icon/tabler/IconDots';
import { IconPlus } from '@/components/icon/tabler/IconPlus';
import { IconX } from '@/components/icon/tabler/IconX';
import {
  brand,
  controlTokens,
  gray,
  revealTokens,
  surfaceTokens,
  typography,
} from '@/theme/themePrimitives';

export const navigationCustomizations: Components<Theme> = {
  MuiSpeedDialIcon: { defaultProps: { icon: <IconPlus />, openIcon: <IconX /> } },
  MuiBreadcrumbs: { defaultProps: { slots: { CollapsedIcon: IconDots } } },
  // Signal 07 menu item: 40px row (product density of the 42px board item), r12, icon in n-500.
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        minHeight: 40,
        gap: 12,
        padding: '0 12px',
        borderRadius: revealTokens.menuItemRadius,
        fontFamily: typography.h1.fontFamily,
        fontSize: 14,
        fontWeight: 500,
        color: gray[800],
        // MUI drops the row to `minHeight: auto` from `sm` up inside a media query, which outranks
        // the plain value above and collapses every desktop menu to the text height.
        [theme.breakpoints.up('sm')]: { minHeight: 40 },
        // MUI also reserves 36px for the icon slot; with the row `gap` that pushed labels ~48px away.
        [`& .${listItemIconClasses.root}`]: { minWidth: 0 },
        // Signal 07 shortcut hint: a trailing <kbd> in the item ("⌘C").
        '& kbd': {
          marginLeft: 'auto',
          paddingLeft: 12,
          fontFamily: typography.h1.fontFamily,
          fontSize: 12,
          fontWeight: 500,
          color: gray[500],
        },
        [`& .${svgIconClasses.root}`]: {
          fontSize: 18,
          color: (theme.vars || theme).palette.text.secondary,
        },
        '&:hover': {
          backgroundColor: revealTokens.itemHover,
          color: surfaceTokens.dark,
          [`& .${svgIconClasses.root}`]: { color: surfaceTokens.dark },
        },
        [`&.${menuItemClasses.focusVisible}`]: {
          backgroundColor: 'transparent',
          boxShadow: `inset 0 0 0 2px ${controlTokens.cobalt}`,
        },
        [`&.${menuItemClasses.selected}`]: {
          backgroundColor: surfaceTokens.interactiveSelected,
          color: surfaceTokens.dark,
          fontWeight: 600,
          '&:hover': { backgroundColor: surfaceTokens.interactivePressed },
          [`&.${menuItemClasses.focusVisible}`]: {
            backgroundColor: surfaceTokens.interactiveSelected,
          },
          '& .MuiTypography-root, & .MuiListItemIcon-root': { color: 'inherit' },
          [`& .${svgIconClasses.root}`]: { color: controlTokens.cobalt },
        },
        // Empty-value placeholder of a Select: selected but never highlighted.
        [`&.${menuItemClasses.selected}[data-value=""]`]: {
          backgroundColor: 'transparent',
          color: (theme.vars || theme).palette.text.secondary,
          fontWeight: 500,
          '&:hover': { backgroundColor: revealTokens.itemHover },
        },
        [`&.${menuItemClasses.disabled}`]: {
          opacity: 1,
          color: revealTokens.itemDisabled,
          [`& .${svgIconClasses.root}`]: { color: surfaceTokens.borderField },
        },
      }),
    },
  },
  // Signal 07 menu panel: r18, 6px padding, e3, paper border.
  MuiMenu: {
    styleOverrides: {
      list: {
        padding: 6,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        [`& .${dividerClasses.root}`]: {
          margin: '5px 6px',
          borderColor: revealTokens.itemDivider,
        },
        '& .MuiListSubheader-root': {
          padding: '12px 12px 6px',
          fontFamily: typography.h1.fontFamily,
          fontSize: 11,
          fontWeight: 600,
          lineHeight: 1,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: gray[500],
        },
      },
      paper: ({ theme }) => ({
        marginTop: 4,
        borderRadius: revealTokens.menuRadius,
        border: `1px solid ${surfaceTokens.borderPaper}`,
        backgroundImage: 'none',
        background: (theme.vars || theme).palette.background.paper,
        boxShadow: surfaceTokens.shadowOverlay,
        ...theme.applyStyles('dark', {
          background: (theme.vars || theme).palette.background.paper,
          borderColor: (theme.vars || theme).palette.divider,
          boxShadow: 'none',
        }),
      }),
    },
  },
  MuiSelect: {
    defaultProps: {
      IconComponent: IconChevronDown,
    },
    styleOverrides: {
      root: () => ({
        // borderRadius: (theme.vars || theme).shape.borderRadius,
        // border: '1px solid',
        // borderColor: gray[200],
        // backgroundColor: (theme.vars || theme).palette.background.paper,
        // boxShadow:
        //   'inset 0 1px 0 1px hsla(220, 0%, 100%, 0.6), inset 0 -1px 0 1px hsla(220, 35%, 90%, 0.5)',
        // '&:hover': {
        //   borderColor: gray[300],
        //   backgroundColor: (theme.vars || theme).palette.background.paper,
        //   boxShadow: 'none',
        // },
        // [`&.${selectClasses.focused}`]: {
        //   outlineOffset: 0,
        //   borderColor: gray[400],
        // },
        // '&:before, &:after': {
        //   display: 'none',
        // },
        cursor: 'pointer',
      }),
      select: ({ theme }) => ({
        marginRight: '16px',
        display: 'flex',
        alignItems: 'center',
        ...theme.applyStyles('dark', {
          display: 'flex',
          alignItems: 'center',
          '&:focus-visible': {
            backgroundColor: gray[900],
          },
        }),
      }),
    },
  },
  MuiLink: {
    defaultProps: {
      underline: 'always',
    },
    variants: [
      {
        props: (props) => (props as { 'data-link-tone'?: string })['data-link-tone'] === 'dark',
        style: {
          color: `${gray[700]} !important`,
          textDecorationColor: alpha(gray[700], 0.35),
          '&:hover': {
            color: `${gray[900]} !important`,
            textDecorationStyle: 'solid',
            textDecorationColor: gray[900],
          },
          '&:has(svg)': {
            borderBottomColor: alpha(gray[700], 0.35),
            '&:hover': {
              borderBottomColor: gray[900],
            },
          },
          '&:focus-visible': {
            outline: `3px solid ${alpha(gray[700], 0.5)}`,
          },
        },
      },
    ],
    styleOverrides: {
      root: () => ({
        color: brand[500],
        fontWeight: 500,
        position: 'relative',
        width: 'fit-content',
        lineHeight: 1.2,
        textDecoration: 'underline dotted',
        textDecorationColor: alpha(brand[500], 0.35),
        textDecorationThickness: '1.5px',
        textUnderlineOffset: '2px',
        transition:
          'text-decoration-color 0.1s ease, text-decoration-style 0.1s ease, color 0.1s ease',
        cursor: 'pointer',
        '&:hover': {
          textDecorationStyle: 'solid',
          textDecorationColor: brand[500],
        },
        '&:has(svg)': {
          textDecoration: 'none',
          borderBottomWidth: '1.5px',
          borderBottomStyle: 'dotted',
          borderBottomColor: alpha(brand[500], 0.35),
          '&:hover': {
            borderBottomStyle: 'solid',
            borderBottomColor: brand[500],
          },
        },
        '&:focus-visible': {
          outline: `3px solid ${alpha(brand[500], 0.5)}`,
          outlineOffset: '4px',
          borderRadius: '2px',
        },
      }),
    },
  },
  // Signal 07: right drawers float; the bottom sheet stays on the edge and carries a handle.
  MuiDrawer: {
    styleOverrides: {
      root: {
        '& .MuiBackdrop-root': { backgroundColor: surfaceTokens.drawerScrim },
      },
      paper: ({ theme }) => ({
        backgroundColor: (theme.vars || theme).palette.background.paper,
        border: 0,
        boxShadow: surfaceTokens.shadowOverlay,
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
        ...theme.applyStyles('dark', { boxShadow: 'none' }),
      }),
      paperAnchorRight: ({ theme }) => ({
        width: '100%',
        borderRadius: 0,
        borderTopLeftRadius: revealTokens.detailDrawerRadius,
        borderBottomLeftRadius: revealTokens.detailDrawerRadius,
        boxShadow: revealTokens.drawerShadowRight,
        // Every right drawer floats. `&&` beats Paper's responsive radius and a
        // one-class paper override. The mobile nav menu opts out in its own sx.
        '&&': {
          [theme.breakpoints.up('sm')]: {
            top: revealTokens.detailDrawerInset,
            right: revealTokens.detailDrawerInset,
            bottom: revealTokens.detailDrawerInset,
            height: 'auto',
            width: revealTokens.detailDrawerWidth,
            maxHeight: `calc(100dvh - ${revealTokens.detailDrawerInset * 2}px)`,
            borderRadius: revealTokens.detailDrawerRadius,
            boxShadow: surfaceTokens.shadowOverlay,
            ...theme.applyStyles('dark', { boxShadow: 'none' }),
          },
        },
        ...theme.applyStyles('dark', { boxShadow: 'none' }),
      }),
      paperAnchorLeft: ({ theme }) => ({
        borderRadius: 0,
        borderTopRightRadius: revealTokens.overlayRadius,
        borderBottomRightRadius: revealTokens.overlayRadius,
        boxShadow: revealTokens.drawerShadowLeft,
        ...theme.applyStyles('dark', { boxShadow: 'none' }),
      }),
      paperAnchorBottom: ({ theme }) => ({
        // `&&` beats Paper's responsive radius and the generic paper size.
        '&&': {
          width: '100%',
          maxHeight: '92dvh',
          borderRadius: 0,
          borderTopLeftRadius: revealTokens.overlayRadius,
          borderTopRightRadius: revealTokens.overlayRadius,
        },
        boxShadow: revealTokens.sheetShadow,
        ...theme.applyStyles('dark', { boxShadow: 'none' }),
        '&::before': {
          content: '""',
          flexShrink: 0,
          alignSelf: 'center',
          width: 40,
          height: 5,
          marginTop: 10,
          borderRadius: 5,
          backgroundColor: revealTokens.sheetHandle,
        },
      }),
    },
  },
  MuiPagination: {
    styleOverrides: {
      root: {
        '& .Mui-selected': {
          borderRadius: '6px',
          padding: '0px',
          minWidth: '20px',
          minHeight: '10px',
          height: '20px',
          background: surfaceTokens.dark,
        },
      },
    },
  },
  MuiPaginationItem: {
    defaultProps: {
      slots: {
        first: IconChevronsLeft,
        previous: IconChevronLeft,
        next: IconChevronRight,
        last: IconChevronsRight,
      },
    },
    styleOverrides: {
      root: ({ theme }) => ({
        '&.MuiPaginationItem-previousNext:not(.Mui-disabled), &.MuiPaginationItem-firstLast:not(.Mui-disabled)':
          {
            color: brand[900],
          },
        '&.Mui-selected': {
          color: 'white',
          backgroundColor: (theme.vars || theme).palette.grey[900],
        },
        ...theme.applyStyles('dark', {
          '&.MuiPaginationItem-previousNext:not(.Mui-disabled), &.MuiPaginationItem-firstLast:not(.Mui-disabled)':
            { color: brand[100] },
          '&.Mui-selected': {
            color: 'black',
            backgroundColor: (theme.vars || theme).palette.grey[50],
          },
        }),
      }),
    },
  },
  // Signal line tabs: the baseline passes through the center of the round-capped indicator.
  MuiTabs: {
    defaultProps: {
      slots: { startScrollButtonIcon: IconChevronLeft, endScrollButtonIcon: IconChevronRight },
    },
    styleOverrides: {
      root: ({ theme }) => ({
        position: 'relative',
        minHeight: 'fit-content',
        '--tabs-divider-color': alpha(surfaceTokens.dark, 0.22),
        '&:not(.MuiTabs-vertical)': { borderBottom: 0 },
        '&:not(.MuiTabs-vertical)::before': {
          content: '""',
          position: 'absolute',
          pointerEvents: 'none',
          left: 0,
          right: 0,
          bottom: (revealTokens.tabIndicatorHeight - 1) / 2,
          height: 1,
          backgroundColor: 'var(--tabs-divider-color)',
        },
        ...theme.applyStyles('dark', {
          '--tabs-divider-color': alpha('#FFFFFF', 0.3),
        }),
      }),
      flexContainer: { gap: 16 },
      indicator: ({ theme }) => ({
        height: revealTokens.tabIndicatorHeight,
        borderRadius: revealTokens.tabIndicatorHeight,
        backgroundColor: surfaceTokens.dark,
        ...theme.applyStyles('dark', {
          backgroundImage: controlTokens.signalGradient,
        }),
      }),
      scrollButtons: {
        width: 28,
        borderRadius: 999,
        '&.Mui-disabled': { opacity: 0.3 },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        minWidth: 0,
        minHeight: 'fit-content',
        padding: '10px 0 13px',
        gap: 6,
        textTransform: 'none',
        fontFamily: typography.h1.fontFamily,
        fontSize: 14,
        fontWeight: 500,
        lineHeight: 1,
        letterSpacing: 0,
        color: (theme.vars || theme).palette.text.secondary,
        borderRadius: 6,
        '&:hover': { color: surfaceTokens.dark },
        '&.Mui-focusVisible': { boxShadow: controlTokens.focusRing },
        [`&.${tabClasses.selected}`]: {
          color: surfaceTokens.dark,
          fontWeight: 600,
        },
        [`&.${tabClasses.disabled}`]: { color: revealTokens.itemDisabled },
        [`& .${svgIconClasses.root}`]: { fontSize: 18, marginBottom: 0 },
        '& > .MuiTab-icon': { margin: 0 },
        ...theme.applyStyles('dark', {
          [`&.${tabClasses.selected}`]: { color: '#fff' },
        }),
      }),
    },
  },
  MuiStepConnector: {
    styleOverrides: {
      line: ({ theme }) => ({
        borderTop: '1px solid',
        borderColor: (theme.vars || theme).palette.divider,
        flex: 1,
        borderRadius: '99px',
      }),
    },
  },
  MuiStepIcon: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: 'transparent',
        border: `1px solid ${gray[400]}`,
        width: 12,
        height: 12,
        borderRadius: '50%',
        '& text': {
          display: 'none',
        },
        '&.Mui-active': {
          border: 'none',
          color: (theme.vars || theme).palette.primary.main,
        },
        '&.Mui-completed': {
          border: 'none',
          color: (theme.vars || theme).palette.success.main,
        },
        ...theme.applyStyles('dark', {
          border: `1px solid ${gray[700]}`,
          '&.Mui-active': {
            border: 'none',
            color: (theme.vars || theme).palette.primary.light,
          },
          '&.Mui-completed': {
            border: 'none',
            color: (theme.vars || theme).palette.success.light,
          },
        }),
        variants: [
          {
            props: { completed: true },
            style: {
              width: 12,
              height: 12,
            },
          },
        ],
      }),
    },
  },
  MuiStepLabel: {
    defaultProps: { StepIconComponent: OutlineStepIcon },
    styleOverrides: {
      label: ({ theme }) => ({
        '&.Mui-completed': {
          opacity: 0.6,
          ...theme.applyStyles('dark', { opacity: 0.5 }),
        },
      }),
    },
  },
  MuiBottomNavigationAction: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        '& .MuiBottomNavigationAction-label': {
          marginTop: '2px',
          fontSize: '0.65rem',
          '&.Mui-selected': {
            fontSize: '0.65rem',
          },
        },
      }),
    },
  },
};
